import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useGetMe, useSetMyTheme, getGetMeQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";

export type Theme = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

type ThemeContextValue = {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
};

const STORAGE_KEY = "eb-theme";

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "system";
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    // ignore
  }
  return "system";
}

function applyTheme(resolved: ResolvedTheme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;
}

export function ThemeProvider({
  children,
  syncWithServer = true,
}: {
  children: ReactNode;
  syncWithServer?: boolean;
}) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => getSystemTheme());

  // Only the staff workspace reads/writes the operator's server preference.
  // Customer and public shop routes retain local/system theme behavior without
  // probing the staff-only /api/me endpoint.
  const meQuery = useGetMe({
    query: {
      queryKey: ["/api/me"],
      staleTime: 60_000,
      retry: false,
      enabled: syncWithServer,
    },
  });
  const persistTheme = useSetMyTheme();
  const qc = useQueryClient();
  const lastSyncedFromServer = useRef<Theme | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemTheme(mq.matches ? "dark" : "light");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const resolvedTheme: ResolvedTheme = theme === "system" ? systemTheme : theme;

  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  // First time we see a server preference, apply it (server is source of truth
  // across devices). Subsequent local changes are pushed back to the server.
  useEffect(() => {
    if (!syncWithServer) return;
    const serverPref = (meQuery.data as { themePreference?: Theme } | undefined)?.themePreference;
    if (!serverPref) return;
    if (lastSyncedFromServer.current === serverPref) return;
    lastSyncedFromServer.current = serverPref;
    if (serverPref !== theme) {
      setThemeState(serverPref);
      try { window.localStorage.setItem(STORAGE_KEY, serverPref); } catch { /* ignore */ }
    }
  }, [meQuery.data, syncWithServer, theme]);

  const setTheme = (next: Theme) => {
    setThemeState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    // Best-effort persist to server (only if signed in / /me succeeded).
    if (syncWithServer && meQuery.data) {
      lastSyncedFromServer.current = next;
      persistTheme.mutate(
        { data: { theme: next } },
        {
          onSuccess: () => qc.invalidateQueries({ queryKey: getGetMeQueryKey() }),
        },
      );
    }
  };

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      resolvedTheme,
      setTheme,
      toggleTheme: () => setTheme(resolvedTheme === "dark" ? "light" : "dark"),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, resolvedTheme, meQuery.data, syncWithServer],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      theme: "system",
      resolvedTheme: "light",
      setTheme: () => undefined,
      toggleTheme: () => undefined,
    };
  }
  return ctx;
}
